import type { InventoryLevel, InventoryMovementKind } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export const ADJUST_KINDS = ['receive', 'adjust', 'count'] as const;
export type AdjustKind = (typeof ADJUST_KINDS)[number];

/** Low stock once what can still be sold is at or below the threshold; no threshold, never low. */
export function isLowStock(level: Pick<InventoryLevel, 'available' | 'reorderPoint'>): boolean {
  return level.reorderPoint !== null && level.available <= level.reorderPoint;
}

/** On hand after the change: a count replaces it, the other kinds add a delta. */
export function projectedOnHand(onHand: number, kind: AdjustKind, quantity: number): number {
  return kind === 'count' ? quantity : onHand + quantity;
}

/** 409 INSUFFICIENT_STOCK carries the on-hand the backend saw, which may differ from ours. */
export function insufficientStock(error: unknown): { onHand: number | null } | null {
  if (!(error instanceof ApiError) || error.code !== 'INSUFFICIENT_STOCK') return null;
  return { onHand: typeof error.meta.onHand === 'number' ? error.meta.onHand : null };
}

/** RESOURCE_IN_USE means stock is still held there; a plain 409 means it became the default. */
export function locationDeleteErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.kind !== 'conflict') return null;
  return error.code === 'RESOURCE_IN_USE'
    ? 'inventory:locations.errors.hasStock'
    : 'inventory:locations.errors.isDefault';
}

/** Movements that raised on-hand, lowered it, or only touched reservations. */
export function movementDirection(
  kind: InventoryMovementKind,
  quantity: number,
): 'in' | 'out' | 'reservation' {
  if (kind === 'reserve' || kind === 'release') return 'reservation';
  return quantity >= 0 ? 'in' : 'out';
}
